import type { WorkingFilters } from "components/types/working-filters";
import { useAppDispatch, useAppSelector } from "../../../util/store-types";
import { updateSingleFilter } from "actions/products-actions";
import { usePersistedFilters } from "components/source/hoc/with-persisted-filters";

/**
 * A hook for accessing and updating a given working filter.
 * Filters are also persisted (in local storage or a cookie) if necessary.
 * @param filter - the name (key) of the working filter in the Redux store.
 * @param onSubmitCallback - any additional actions to take once filters are updated (i.e. fetching products or reloading a grid).
 * @returns an array containing the filter value, and a setter for that value.
 */
export const useWorkingFilter = <T extends keyof WorkingFilters>(
  filter: T,
  onSubmitCallback: (updatedFilterValue?: WorkingFilters[T]) => void = () => {}
): [WorkingFilters[T], (filter: WorkingFilters[T]) => void] => {
  const filterValue = useAppSelector(state => state.workingFilters?.[filter]);
  const dispatch = useAppDispatch();
  const { writePreference } = usePersistedFilters();

  const updateWorkingFilter = (updatedFilterValue: WorkingFilters[T]) => {
    dispatch(updateSingleFilter(filter, updatedFilterValue));
  };

  const persistFilter = (updatedFilterValue: WorkingFilters[T]) => {
    writePreference(filter, updatedFilterValue);
  };

  const updateFilter = (updatedFilterValue: WorkingFilters[T]) => {
    updateWorkingFilter(updatedFilterValue);
    persistFilter(updatedFilterValue);
    onSubmitCallback(updatedFilterValue);
  };

  return [filterValue, updateFilter];
};
